import { useContext, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import Button from '../common/Button';

const Header = () => {
  const { user, logout } = useContext(AuthContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  const navigation = [];

  // Show "Schedule" for gym_manager role
  if (user?.role === 'gym_manager') {
    navigation.push({ name: 'Schedule', href: '/dashboard', current: false });
  }

  // Only show "Admin" for admin/gym_manager roles
  if (['admin', 'gym_manager'].includes(user?.role)) {
    navigation.push({ name: 'Admin', href: '/admin', current: false });
  }

  // Determine home page URL based on role
  const homeUrl = user?.role === 'platform_admin' ? '/admin/console' : '/dashboard';

  return (
    <nav className="bg-white border-b-4 border-slate-900 shadow-retro">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            {/* Logo */}
            <a href={homeUrl} className="flex-shrink-0 flex items-center hover:opacity-80 transition-all">
              <h1 className="text-xl font-bold bg-gradient-to-r from-teal-600 via-purple-600 to-magenta-600 bg-clip-text text-transparent">
                BoardRez
              </h1>
            </a>


            {/* Desktop Navigation */}
            <div className="hidden sm:ml-6 sm:flex sm:space-x-4">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-slate-600 hover:text-teal-600 hover:bg-teal-50 inline-flex items-center px-3 py-2 rounded-lg text-sm font-semibold transition-all border-2 border-transparent hover:border-teal-200"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </div>

          {/* User Menu */}
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-lg px-3 py-2 hover:bg-teal-50 border-2 border-transparent hover:border-teal-200 transition-all"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold border-2 border-slate-900 shadow-retro">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-semibold text-slate-700">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="text-xs text-slate-500">
                      {user?.role}
                    </div>
                  </div>
                </div>
                <svg className="h-5 w-5 text-slate-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Dropdown menu */}
              {isUserMenuOpen && (
                <>
                  {/* Backdrop to close menu when clicking outside */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 rounded-lg bg-white border-2 border-slate-900 shadow-retro z-20">
                    <div className="py-1">
                      {/* Show "My Reservations" for gym members (not admin, gym_manager, platform_admin, or chain_owner) */}
                      {!['admin', 'gym_manager', 'platform_admin', 'chain_owner'].includes(user?.role) && (
                        <a
                          href="/reservations"
                          className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-700 font-medium transition-colors"
                        >
                          <svg className="mr-3 h-5 w-5 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          My Reservations
                        </a>
                      )}
                      <a
                        href="/settings"
                        className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-700 font-medium transition-colors"
                      >
                        <svg className="mr-3 h-5 w-5 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Settings
                      </a>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-coral-50 hover:text-coral-700 text-left font-medium transition-colors"
                      >
                        <svg className="mr-3 h-5 w-5 text-coral-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="sm:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-lg text-slate-600 hover:text-teal-600 hover:bg-teal-50 border-2 border-transparent hover:border-teal-200 transition-all"
            >
              <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="sm:hidden border-t-2 border-slate-200">
          <div className="pt-2 pb-3 space-y-1">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-slate-600 hover:bg-teal-50 hover:text-teal-700 block pl-3 pr-4 py-2 border-l-4 border-transparent hover:border-teal-500 text-base font-semibold transition-all"
              >
                {item.name}
              </a>
            ))}
            <div className="border-t-2 border-slate-200 pt-4 pb-3">
              <div className="flex items-center px-4">
                <div className="flex-1">
                  <div className="text-base font-semibold text-slate-800">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="text-sm font-medium text-slate-500">
                    {user?.email}
                  </div>
                </div>
              </div>
              <div className="mt-3 px-4 space-y-2">
                {/* Show "My Reservations" for gym members (not admin, gym_manager, platform_admin, or chain_owner) */}
                {!['admin', 'gym_manager', 'platform_admin', 'chain_owner'].includes(user?.role) && (
                  <a
                    href="/reservations"
                    className="block px-4 py-2 text-base font-semibold text-slate-700 hover:bg-teal-50 hover:text-teal-700 rounded-lg transition-colors"
                  >
                    My Reservations
                  </a>
                )}
                <a
                  href="/settings"
                  className="block px-4 py-2 text-base font-semibold text-slate-700 hover:bg-purple-50 hover:text-purple-700 rounded-lg transition-colors"
                >
                  Settings
                </a>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  size="small"
                  className="w-full"
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;